import { jsxs, jsx } from 'react/jsx-runtime';
import { Button } from '../Button.js';
import { preventForwardPropsConfig } from '../../utils/preventForwardProps.js';
import React__default from 'react';
import { css, styled } from 'styled-components';
import { cssVar } from '../../theme/variables.js';
import { Flex } from '../Flex.js';
import { mediaMobile, cssTextEllipsis } from '../../constants/styled-utils.js';
import { useResponsive } from '../../hooks/useResponsive.js';
import { IconNavigational24Cross, IconNavigational24Search } from '@fabric-app/icons/svg';
import { motion } from 'framer-motion';

const variants = {
  /**
  * A more discreet variant of the text input that blends more with the UI.
  * Currently used for the optional title of notepads in mobile.
  */
  discreet: css([
    `background:transparent;border-radius:0.375rem;border:none;font-weight:400;&[data-empty='true']{background:`,
    `;}&:focus-within{border:none;box-shadow:none;}`
  ], cssVar["color-bg-secondary-button"]),
  /**
  * The default variant is more obvious and contrasts well with the UI.
  * has outline and primary bg
  */
  default: "",
  /**
  *
  */
  secondary: css([
    `background:`,
    `;border:none;&::placeholder{color:`,
    `;}&:focus-within{border:none;box-shadow:none;}`
  ], cssVar["color-bg-section"], cssVar["color-text-placeholder"]),
  auth: css([
    `width:100%;height:44px;color:`,
    `;font-size:15px;font-weight:500;letter-spacing:0px;text-align:left;background-color:`,
    `;border-radius:16px;padding:0 16px;&::placeholder{color:rgba(`,
    `,0.3);}outline:none;border:none;&:focus-within{border:none;outline:`,
    ` 1px solid;box-shadow:none;}`
  ], cssVar["color-text-primary"], cssVar["color-border-tertiary"], cssVar["color-text-primary-rgb"], cssVar["color-border-primary"])
};
const cssPropsBasedOnHeight = css([
  ``,
  ``
], (p) => {
  switch (p.inputHeight) {
    case "xl":
      return css([
        `padding-left:1rem;font-size:0.9375rem;font-weight:500;height:`,
        `;`,
        `{height:50px;}`
      ], cssVar["height-input-xl"], mediaMobile);
    case "lg":
      return css([
        `height:`,
        `;`,
        `{height:48px;}`
      ], cssVar["height-input-lg"], mediaMobile);
    case "sm":
      return css([
        `height:`,
        `;`
      ], cssVar["height-input-sm"]);
    default:
      return css([
        `height:`,
        `;`,
        `{height:48px;}`
      ], cssVar["height-input"], mediaMobile);
  }
});
const cssFocusState = css([
  `border:1px solid `,
  `;box-shadow:0px 1px 34px 0px rgba(42,55,238,0.14);`
], cssVar["color-app-primary"]);
const InputContainer = styled(motion(Flex)).attrs(({ isEmpty, ...props }) => {
  return {
    gap: "element",
    alignItems: "center",
    "data-empty": isEmpty,
    ...props
  };
}).withConfig(preventForwardPropsConfig([
  "isEmpty",
  "inputHeight",
  "variant"
])).withConfig({
  displayName: "TextInput__InputContainer",
  componentId: "sc-d4c17cc3-0"
})([
  `padding:0px 0.75rem;min-width:200px;color:`,
  `;background:`,
  `;`,
  `;border-radius:13px;border:1px solid `,
  `;`,
  ` `,
  ` `,
  `{width:100%;}`,
  ` `,
  ``
], cssVar["color-text-secondary"], cssVar["color-bg-primary"], cssPropsBasedOnHeight, cssVar["color-border-primary"], (p) => !p.readOnly && css([
  `&:focus-within{`,
  `}`
], cssFocusState), (p) => p.forceFocusState && cssFocusState, mediaMobile, (p) => p.variant && variants[p.variant], (p) => p.disabled && css([
  `background:`,
  `;cursor:not-allowed;`
], cssVar["color-bg-tertiary"]));
const Input = styled.input.attrs((props) => {
  const { isMobileView } = useResponsive();
  return {
    type: "text",
    ...props,
    autoFocus: !isMobileView && props.autoFocus
  };
}).withConfig({
  displayName: "TextInput__Input",
  componentId: "sc-d4c17cc3-1"
})([
  `width:100%;background:transparent;font-size:15px;color:`,
  `;&:focus{outline:none;}&::placeholder{color:`,
  `;}&:disabled{color:`,
  `;cursor:not-allowed;}`,
  `;`
], cssVar["color-text-primary"], cssVar["color-text-placeholder"], cssVar["color-text-quaternary"], cssTextEllipsis);
const TextInputBase = /* @__PURE__ */ React__default.forwardRef(function TextInputBase2({ startAdornment, endAdornment, inputHeight, variant, containerProps, readOnly, disabled, ...props }, ref) {
  return /* @__PURE__ */ jsxs(InputContainer, {
    inputHeight,
    variant,
    isEmpty: !props.value,
    readOnly,
    disabled,
    ...containerProps,
    children: [
      startAdornment,
      /* @__PURE__ */ jsx(Input, {
        ref,
        readOnly,
        disabled,
        ...props
      }),
      endAdornment
    ]
  });
});
const TextInput = Object.assign(TextInputBase, {
  SearchIcon: IconNavigational24Search,
  ClearIcon: IconNavigational24Cross
});
const ClearInputButton = styled(Button).attrs({
  shape: "square",
  variant: "icon",
  size: "sm"
}).withConfig({
  displayName: "TextInput__ClearInputButton",
  componentId: "sc-d4c17cc3-2"
})([
  `outline:none !important;`
]);
const AuthTextInput = styled(TextInput).attrs((p) => ({
  variant: "auth",
  ...p
})).withConfig({
  displayName: "TextInput__AuthTextInput",
  componentId: "sc-d4c17cc3-3"
})([
  ``
]);

export { AuthTextInput, ClearInputButton, InputContainer, TextInput };
//# sourceMappingURL=TextInput.js.map
